import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';

class CartOffersComponent extends HTMLElement {
  connectedCallback() {
    this.sectionId =
      this.dataset.sectionId ||
      this.closest('cart-items-component')?.dataset?.sectionId ||
      this.closest('.shopify-section')?.id?.replace('shopify-section-', '') ||
      'sections--20234126164155__header_section';

    this.applyBtn = this.querySelector('[data-action="apply"]');
    this.removeBtn = this.querySelector('[data-action="remove"]');
    this.subscribeToggle = this.querySelector('.cart-offers__subscribe-toggle');
    this.subscribePanel = this.querySelector('.cart-offers__sub-panel');
    this.subForm = this.querySelector('.cart-offers__sub-form');
    this.manualForm = this.querySelector('.cart-offers__manual-form');
    this.feedback = this.querySelector('.cart-offers__feedback');

    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', this.handleApply);
    }
    if (this.removeBtn) {
      this.removeBtn.addEventListener('click', this.handleRemove);
    }
    this.querySelectorAll('[data-action="remove-custom"]').forEach((btn) => {
      btn.addEventListener('click', this.handleRemove);
    });
    if (this.subscribeToggle && this.subscribePanel) {
      this.subscribeToggle.addEventListener('click', this.toggleSubscribePanel);
    }
    if (this.subForm) {
      this.subForm.addEventListener('submit', this.handleSubscribe);
    }
    if (this.manualForm) {
      this.manualForm.addEventListener('submit', this.handleManualApply);
    }
  }

  toggleSubscribePanel = () => {
    if (!this.subscribeToggle || !this.subscribePanel) return;
    const isExpanded = this.subscribeToggle.getAttribute('aria-expanded') === 'true';
    this.subscribeToggle.setAttribute('aria-expanded', String(!isExpanded));
    this.subscribePanel.classList.toggle('hidden', isExpanded);
    if (!isExpanded) {
      const input = this.subscribePanel.querySelector('input[type="email"]');
      input?.focus();
    }
  };

  showFeedback(message, isError = false) {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    this.feedback.className = `cart-offers__feedback ${isError ? 'cart-offers__feedback--error' : 'cart-offers__feedback--success'}`;
    this.feedback.classList.remove('hidden');
  }

  setLoading(btn, isLoading, text = '') {
    if (!btn) return;
    btn.disabled = isLoading;
    const spinner = btn.querySelector('.cart-offers__spinner, .cart-offers__sub-spinner, .cart-offers__manual-spinner');
    const label = btn.querySelector('.cart-offers__btn-text, .cart-offers__sub-btn-text, .cart-offers__manual-btn-text');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
    if (label && text) label.textContent = text;
  }

  async applyDiscountCode(code, triggerBtn) {
    code = (code || '').trim().toUpperCase();
    if (!code) {
      this.showFeedback('Please enter a coupon code.', true);
      return;
    }

    this.setLoading(triggerBtn, true, 'Applying...');

    try {
      // 1. Set the Shopify checkout discount session cookie
      await fetch(`/discount/${encodeURIComponent(code)}`).catch(() => {});

      // 2. Call Cart update API with discount code
      const updateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
      const body = {
        discount: code,
      };
      if (this.sectionId) {
        body.sections = [this.sectionId];
      }

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      // Check if discount was applicable
      if (data.discount_codes && Array.isArray(data.discount_codes)) {
        const matching = data.discount_codes.find((d) => d.code.toUpperCase() === code);
        if (matching && matching.applicable === false) {
          this.showFeedback(`Coupon "${code}" is not valid or not applicable.`, true);
          this.setLoading(triggerBtn, false, 'Apply');
          return;
        }
      }

      // Dispatch event to inform other theme components
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id || 'cart-offers'));

      // Morph the section to reflect discounts and totals
      if (this.sectionId && data.sections && data.sections[this.sectionId]) {
        if (typeof morphSection === 'function') {
          try {
            await morphSection(this.sectionId, data.sections[this.sectionId]);
          } catch (mErr) {
            console.warn('[CartOffers] Morph error, falling back:', mErr);
            window.location.reload();
            return;
          }
        }
      } else {
        window.location.reload();
        return;
      }

      // Re-ensure drawer stays open
      setTimeout(() => {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      }, 50);
    } catch (err) {
      console.error('Error applying coupon:', err);
      this.showFeedback('Unable to apply coupon. Please try again.', true);
      this.setLoading(triggerBtn, false, 'Apply');
    }
  }

  handleApply = (e) => {
    e?.preventDefault();
    const code = this.applyBtn?.dataset?.code || 'AAKRUTI10';
    this.applyDiscountCode(code, this.applyBtn);
  };

  handleManualApply = (e) => {
    e?.preventDefault();
    const input = this.manualForm?.querySelector('input[name="manual_coupon"]');
    const submitBtn = this.manualForm?.querySelector('button[type="submit"]');
    const code = input?.value?.trim();
    this.applyDiscountCode(code, submitBtn);
  };

  handleRemove = async (e) => {
    e?.preventDefault();
    const btn = e.currentTarget || this.removeBtn;
    this.setLoading(btn, true);

    try {
      await fetch('/discount/CLEAR').catch(() => {});

      const updateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
      const body = {
        discount: '',
      };
      if (this.sectionId) {
        body.sections = [this.sectionId];
      }

      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id || 'cart-offers'));

      if (this.sectionId && data.sections && data.sections[this.sectionId]) {
        if (typeof morphSection === 'function') {
          try {
            await morphSection(this.sectionId, data.sections[this.sectionId]);
          } catch (mErr) {
            window.location.reload();
            return;
          }
        }
      } else {
        window.location.reload();
        return;
      }

      // Re-ensure drawer stays open
      setTimeout(() => {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      }, 50);
    } catch (err) {
      console.error('Error removing coupon:', err);
      this.setLoading(btn, false);
    }
  };

  handleSubscribe = async (e) => {
    e?.preventDefault();
    const emailInput = this.subForm?.querySelector('input[name="email"]');
    const email = emailInput?.value?.trim();
    if (!email) return;

    const subSubmitBtn = this.subForm?.querySelector('.cart-offers__sub-btn');
    this.setLoading(subSubmitBtn, true, 'Subscribing...');

    try {
      const formData = new FormData();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('contact[tags]', 'newsletter');
      formData.append('contact[email]', email);

      await fetch('/contact#contact_form', {
        method: 'POST',
        body: formData,
      }).catch(() => {});

      await this.applyDiscountCode('AAKRUTI10', subSubmitBtn);
    } catch (err) {
      console.error('Error subscribing & applying:', err);
      this.showFeedback('Subscription error. Please try again.', true);
      this.setLoading(subSubmitBtn, false, 'Subscribe & Apply');
    }
  };
}

if (!customElements.get('cart-offers-component')) {
  customElements.define('cart-offers-component', CartOffersComponent);
}
